/**
 * Stale-While-Revalidate (SWR) caching engine.
 *
 * Built on top of `idbCache` — serves cached data instantly from IndexedDB,
 * then revalidates from the source in the background. If the fresh data
 * differs from the cached version, the `onRevalidate` callback fires so
 * the caller (typically a Zustand store) can update its state.
 *
 * Features:
 * - Request deduplication — concurrent calls for the same key share one fetch
 * - Background revalidation — errors are swallowed, stale data continues to serve
 * - Type-safe — full TypeScript generics
 * - Zero dependencies beyond idbCache
 */

import { idbCache, type StoreName } from './idb-cache';

// ── Revalidation state tracking ─────────────────────────────────
// Tracks active background revalidations so UI can show loading indicators.

const _revalidatingKeys = new Set<string>();
type RevalListener = (keys: Set<string>) => void;
const _revalListeners = new Set<RevalListener>();

/** Subscribe to revalidation state changes. Returns unsubscribe function. */
export function onRevalidationChange(listener: RevalListener): () => void {
  _revalListeners.add(listener);
  return () => { _revalListeners.delete(listener); };
}

/** Check if a specific cache key pattern is currently revalidating */
export function isRevalidating(pattern?: string): boolean {
  if (!pattern) return _revalidatingKeys.size > 0;
  for (const k of _revalidatingKeys) {
    if (k.includes(pattern)) return true;
  }
  return false;
}

function _setRevalidating(key: string, active: boolean) {
  if (active) _revalidatingKeys.add(key);
  else _revalidatingKeys.delete(key);
  for (const l of _revalListeners) { try { l(_revalidatingKeys); } catch {} }
}

// ── Types ────────────────────────────────────────────────────────

export interface SWRConfig<T> {
  /** Cache key — use cacheKeys.* helpers from cache-keys.ts */
  key: string;
  /** IDB object store to use */
  store: StoreName;
  /** Async function that fetches fresh data from the source */
  fetcher: () => Promise<T>;
  /** Cache TTL in milliseconds — use TTL.* constants from cache-keys.ts */
  ttl: number;
  /** Called when background revalidation produces data that differs from cache */
  onRevalidate?: (data: T) => void;
  /** Called if background revalidation fails (stale data continues to serve) */
  onError?: (error: Error) => void;
  /** Bypass cache read — force fresh fetch (still writes result to cache) */
  skipCache?: boolean;
  /** Skip background revalidation if cache entry is younger than this (ms).
   *  Prevents unnecessary API calls when data was just cached. Default: 0 (always revalidate) */
  staleTime?: number;
}

// ── In-flight deduplication ──────────────────────────────────────

const inflight = new Map<string, Promise<unknown>>();

// ── Core SWR function ────────────────────────────────────────────

/**
 * Fetch data with stale-while-revalidate semantics.
 *
 * 1. If cached data exists in IDB → return it immediately
 * 2. Fire background revalidation → call fetcher, compare, call onRevalidate if different
 * 3. If no cache → await fetcher, cache result, return
 *
 * Concurrent calls for the same key are deduplicated — only one fetch runs.
 */
export async function swrFetch<T>(config: SWRConfig<T>): Promise<T> {
  const { key, store, fetcher, ttl, onRevalidate, onError, skipCache, staleTime } = config;

  // ── Step 1: Check cache (unless skipCache) ──
  if (!skipCache) {
    const entry = await idbCache.getEntry<T>(store, key);
    if (entry != null) {
      console.log(`[SWR] cache hit: ${key}`);
      // Skip revalidation if entry is fresh (younger than staleTime)
      const age = Date.now() - entry.createdAt;
      if (staleTime && age < staleTime) {
        console.log(`[SWR] fresh (${Math.round(age / 1000)}s < ${Math.round(staleTime / 1000)}s): ${key}`);
        return entry.data;
      }
      // Fire background revalidation (don't await)
      revalidate(key, store, fetcher, ttl, entry.data, onRevalidate, onError);
      return entry.data;
    }
  }

  // ── Step 2: Cache miss — fetch fresh ──
  // Deduplicate concurrent fetches for the same key
  const existing = inflight.get(key);
  if (existing) {
    console.log(`[SWR] dedup: ${key}`);
    return existing as Promise<T>;
  }

  const promise = (async () => {
    try {
      console.log(`[SWR] fetch: ${key}`);
      const fresh = await fetcher();
      // Write to cache
      if (fresh != null) {
        idbCache.set(store, key, fresh, ttl).catch(() => {});
      }
      return fresh;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
}

// ── Background revalidation ──────────────────────────────────────

function revalidate<T>(
  key: string,
  store: StoreName,
  fetcher: () => Promise<T>,
  ttl: number,
  cached: T,
  onRevalidate?: (data: T) => void,
  onError?: (error: Error) => void,
) {
  // Deduplicate — if already revalidating this key, skip
  const revalKey = `__reval__${key}`;
  if (inflight.has(revalKey)) return;

  _setRevalidating(key, true);

  const promise = (async () => {
    try {
      const fresh = await fetcher();
      // Compare with cached — only notify if different
      const cachedJson = JSON.stringify(cached);
      const freshJson = JSON.stringify(fresh);
      if (freshJson !== cachedJson) {
        console.log(`[SWR] revalidated (changed): ${key}`);
        // Update cache
        if (fresh != null) {
          idbCache.set(store, key, fresh, ttl).catch(() => {});
        } else {
          idbCache.delete(store, key).catch(() => {});
        }
        // Notify caller
        onRevalidate?.(fresh);
      } else {
        console.log(`[SWR] revalidated (unchanged): ${key}`);
        // Refresh TTL even if data is unchanged
        if (fresh != null) {
          idbCache.set(store, key, fresh, ttl).catch(() => {});
        }
      }
    } catch (err) {
      console.warn(`[SWR] revalidation failed: ${key}`, err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      _setRevalidating(key, false);
      inflight.delete(revalKey);
    }
  })();

  inflight.set(revalKey, promise);
}

// ── Cache invalidation ───────────────────────────────────────────

/** Delete a specific cache entry. Also cancels any in-flight fetch for this key. */
export async function invalidate(key: string, store: StoreName = 'org_data'): Promise<void> {
  inflight.delete(key);
  inflight.delete(`__reval__${key}`);
  await idbCache.delete(store, key);
  console.log(`[SWR] invalidated: ${key}`);
}

/** Delete all cache entries whose key contains the given pattern. */
export async function invalidateByPattern(pattern: string, store: StoreName = 'org_data'): Promise<void> {
  // Clear in-flight entries matching pattern
  for (const k of inflight.keys()) {
    if (k.includes(pattern)) inflight.delete(k);
  }
  await idbCache.clearByKeyPattern(store, pattern);
  console.log(`[SWR] invalidated pattern: ${pattern} (store: ${store})`);
}
